const PADDLE_SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";

let paddleScriptPromise = null;
let paddleInitializePromise = null;
let paddleInitialized = false;

const getEnvValue = (key) => {
  if (typeof process === "undefined" || !process.env) {
    return "";
  }

  return process.env[key] || "";
};

function loadPaddleScript() {
  if (window.Paddle) {
    return Promise.resolve(window.Paddle);
  }

  if (paddleScriptPromise) {
    return paddleScriptPromise;
  }

  paddleScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${PADDLE_SCRIPT_URL}"]`,
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.Paddle), {
        once: true,
      });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Unable to load Paddle checkout.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.Paddle);
    script.onerror = () => reject(new Error("Unable to load Paddle checkout."));
    document.body.appendChild(script);
  });

  return paddleScriptPromise;
}

function handlePaddleEvent(event) {
  if (event?.name !== "checkout.completed") {
    return;
  }

  try {
    window.Paddle?.Checkout?.close();
  } catch {
    // Ignore close errors and continue redirecting.
  }

  window.location.href = "/createposts?checkout=success";
}

export async function initializePaddle() {
  if (paddleInitialized) {
    return window.Paddle;
  }

  if (paddleInitializePromise) {
    return paddleInitializePromise;
  }

  paddleInitializePromise = (async () => {
    const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

    if (!token) {
      throw new Error("Paddle checkout is not configured.");
    }

    await loadPaddleScript();

    if (!window.Paddle) {
      throw new Error("Paddle checkout is unavailable. Please try again.");
    }

    const paddleEnvironment = process.env.REACT_APP_PADDLE_ENVIRONMENT;

    if (paddleEnvironment === "sandbox") {
      window.Paddle.Environment.set("sandbox");
    }

    // window.Paddle.Initialize({ token });
    window.Paddle.Initialize({
      token,
      eventCallback: handlePaddleEvent,
    });
    paddleInitialized = true;

    return window.Paddle;
  })().catch((error) => {
    paddleInitializePromise = null;
    throw error;
  });

  return paddleInitializePromise;
}

export async function openPaddleCheckout(priceId) {
  if (!priceId) {
    throw new Error(
      "This plan is missing a checkout price. Please try again later.",
    );
  }

  if (!priceId.startsWith("pri_")) {
    throw new Error("This plan has an invalid Paddle price ID.");
  }

  await initializePaddle();

  window.Paddle.Checkout.open({
    settings: {
      displayMode: "overlay",
      theme: "light",
      locale: "en",
    },
    items: [
      {
        priceId,
        quantity: 1,
      },
    ],
  });
}

export async function openPaddleCheckoutForTransaction(transactionId) {
  if (!transactionId) {
    throw new Error("Checkout transaction ID missing from server response.");
  }

  await initializePaddle();

  window.Paddle.Checkout.open({
    transactionId,
    settings: {
      displayMode: "overlay",
      theme: "light",
      locale: "en",
    },
  });
}
